import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ChallengesListResponse,
  DeleteChallengeResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";

const buildDeleteChallengeUrl = (challengeId: string): string =>
  API_ROUTES.DELETE_CHALLENGE.replace("{challenge_id}", challengeId);

/** Variables fed to `useDeleteChallenge.mutateAsync`. `seasonId` is
 *  optional and used purely to scope the cache update / invalidation to
 *  the season's challenge listing when the caller knows it. */
export type DeleteChallengeVariables = {
  challengeId: string;
  seasonId?: string | null;
};

const deleteChallenge = async ({
  challengeId,
}: DeleteChallengeVariables): Promise<string> => {
  // Per the staff-side delete convention (see `useDeleteSeason`), this
  await getPrivateInstance().delete<DeleteChallengeResponse>(
    buildDeleteChallengeUrl(challengeId),
  );
  return challengeId;
};

/**
 * React Query mutation for `DELETE /game-pass-admin/v2/admin/challenges/{id}`.
 *
 * On success it optimistically drops the challenge from any cached
 * challenges listing (so the Season Details editor reflects the removal
 * without waiting on a refetch), then invalidates the challenges list +
 * seasons list so the per-card `challenge_count` metric stays in sync.
 */
export const useDeleteChallenge = (): UseMutationResult<
  string,
  Error,
  DeleteChallengeVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "challenges", "delete"],
    mutationFn: deleteChallenge,
    onSuccess: (challengeId, variables) => {
      queryClient.setQueriesData<ChallengesListResponse>(
        { queryKey: ["game-pass", "challenges", "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            challenges: current.challenges.filter(
              (record) =>
                (record.id ?? record.challenge_id) !== challengeId,
            ),
          };
        },
      );
      if (variables.seasonId) {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "challenges", "list", variables.seasonId],
        });
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "seasons", "detail", variables.seasonId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "challenges", "list"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};

export { deleteChallenge };
